// What editing a group starts from (S112): the group, its pinned version's requirements (from
// the group's own requirements view, which its owner may read) and the workflow's name and
// version — from the version index for admins, else the marketplace when the pinned version
// is the latest — as a draft once everything has loaded.
import { queryOptions, useQuery } from "@tanstack/react-query";

import { api } from "../../api/client";
import { requestJson } from "../../api/http";
import { unwrap } from "../../api/queries";
import { taskGroupQuery } from "../api";
import { useLookups } from "../lookups";
import type { TaskGroup } from "../model";
import type { Draft, MarketplaceWorkflow } from "./draft";
import { draftFromGroup, pinnedWorkflow } from "./edit";

const rawRequirementsQuery = (id: string) =>
  queryOptions({
    queryKey: ["task-groups", id, "requirements", "raw"],
    queryFn: async () => (await unwrap(api.GET("/api/task-groups/{task_group_id}/requirements", { params: { path: { task_group_id: id } } }))).requirements.map((r) => r.requirement),
  });

const listedQuery = (taskTypeId: string) =>
  queryOptions({
    queryKey: ["marketplace", "workflows", taskTypeId, "all"],
    queryFn: async () => (await requestJson("GET", `/api/marketplace/workflows?task_type=${encodeURIComponent(taskTypeId)}&limit=200`)) as MarketplaceWorkflow[],
    enabled: taskTypeId !== "",
  });

export function useEditGroup(groupId: string, fallbackName: string) {
  const group = useQuery(taskGroupQuery(groupId));
  const requirements = useQuery(rawRequirementsQuery(groupId));
  const listed = useQuery(listedQuery(group.data?.task_type_id ?? ""));
  const lookups = useLookups();
  const g: TaskGroup | undefined = group.data;
  let draft: Draft | undefined;
  if (g && requirements.data && !listed.isPending) {
    const indexed = lookups.version(g.workflow_version_id);
    const latest = listed.data?.find((w) => w.version.id === g.workflow_version_id);
    const name = indexed?.workflowName ?? latest?.name ?? fallbackName;
    const version = indexed?.version ?? latest?.version.version ?? 0;
    draft = draftFromGroup(g, pinnedWorkflow(g, name, version, requirements.data));
  }
  const error = group.error ?? requirements.error;
  return { group: g, draft, error };
}
